import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { applyDocumentMeta, DEFAULT_SITE_DESCRIPTION, resolveRouteMeta, type RouteMeta } from '@/utils/pageMeta';

const ROUTE_META: Record<string, RouteMeta> = {
  '/': { title: 'Workspace', description: DEFAULT_SITE_DESCRIPTION },
  '/login': {
    title: 'Sign in',
    description:
      'Sign in to CoTaskr — Plan, CodeFx, Ship, and Lenc. One workspace for delivery, engineering, releases, and ops.',
  },
  '/register': {
    title: 'Create organization',
    description:
      'Start your organization on CoTaskr. Plan is included from day one; add Code, Ship, and Lenc as you grow.',
  },
  '/forgot-password': { title: 'Forgot password', description: 'Reset your CoTaskr account password.' },
  '/reset-password': { title: 'Reset password', description: 'Choose a new password for your CoTaskr account.' },
  '/dashboard': {
    title: 'Dashboard',
    description: 'CoTaskr Plan home — today’s follow-ups, projects, and team activity at a glance.',
  },
  '/projects': { title: 'Projects', description: 'Manage projects and delivery work in CoTaskr Plan.' },
  '/board': { title: 'Board', description: 'Kanban boards for agile delivery in CoTaskr Plan.' },
  '/backlog': { title: 'Backlog', description: 'Prioritize epics, stories, and tasks in CoTaskr Plan.' },
  '/list': { title: 'List view', description: 'Spreadsheet-style work list in CoTaskr Plan.' },
  '/sprints': { title: 'Sprints', description: 'Plan, start, and complete sprints in CoTaskr Plan.' },
  '/time-tracking': { title: 'Timesheet', description: 'Log time and submit timesheets in CoTaskr Plan.' },
  '/calendar': { title: 'Calendar', description: 'Calendar view for deadlines and team schedules.' },
  '/gantt': { title: 'Schedule', description: 'Gantt timeline and dependencies in CoTaskr Plan.' },
  '/workload': { title: 'Team capacity', description: 'See who is overloaded and balance work across the team.' },
  '/reports': { title: 'Reports', description: 'Velocity, burndown, and delivery reports in CoTaskr Plan.' },
  '/stakeholder': { title: 'Executive summary', description: 'Executive roll-up of progress and risk.' },
  '/economics': { title: 'Project economics', description: 'Budgets, rates, and cost tracking in CoTaskr Plan.' },
  '/cost': { title: 'CoTaskr Cost', description: 'Unified spend across CoTaskr products — labor, AI, cloud, and licenses.' },
  '/ai-studio': { title: 'AI Studio', description: 'AI-assisted planning and task creation in CoTaskr Plan.' },
  '/ai-whiteboard': { title: 'Whiteboard', description: 'Collaborative AI whiteboard for discovery and planning.' },
  '/ai-assistant': { title: 'AI Assistant', description: 'Ask CoTaskr AI about your projects and work items.' },
  '/handbook': { title: 'Handbook', description: 'Team wiki and handbook in CoTaskr Plan.' },
  '/members': { title: 'Org members', description: 'Invite and manage people in your CoTaskr organization.' },
  '/billing': {
    title: 'Billing',
    description: 'Subscriptions for CoTaskr Plan, CodeFx, Ship, and Lenc — trials, seats, and invoices.',
  },
  '/admin/org-settings': { title: 'Organization settings', description: 'Branding, hierarchy, and tenant preferences.' },
  '/admin/audit-logs': { title: 'Audit logs', description: 'Organization-wide audit trail for admins.' },
  '/admin': { title: 'Org management', description: 'Platform administration for CoTaskr operators.' },
  '/integrations': { title: 'Integrations', description: 'Connect CoTaskr Plan to Slack, GitHub, and more.' },
  '/webhooks': { title: 'Webhooks', description: 'Outbound webhooks for CoTaskr events.' },
  '/workflows': { title: 'Workflows', description: 'Automate actions across CoTaskr Plan.' },
  '/roles': { title: 'Roles & permissions', description: 'Custom roles and access control.' },
  '/settings': { title: 'Settings', description: 'Your CoTaskr profile, locale, and preferences.' },
  '/notifications': { title: 'Notifications', description: 'Mentions, assignments, and activity alerts.' },
  '/ai-usage': { title: 'AI usage', description: 'AI consumption across CoTaskr products.' },
};

export default function PageMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    applyDocumentMeta(resolveRouteMeta(pathname, ROUTE_META));
  }, [pathname]);

  return null;
}
